import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getInvoices, updateInvoiceStatus, deleteInvoice } from '../../api/finance';

const fmt = (amount, currency = 'TRY') =>
  new Intl.NumberFormat('tr-TR', { style: 'currency', currency }).format(amount ?? 0);

const STATUS_LABELS = {
  draft:     { label: 'Taslak',         color: 'bg-gray-100 text-gray-600' },
  sent:      { label: 'Gönderildi',     color: 'bg-blue-100 text-blue-700' },
  awaiting:  { label: 'Beklemede',      color: 'bg-yellow-100 text-yellow-700' },
  collected: { label: 'Tahsil Edildi',  color: 'bg-green-100 text-green-700' },
  overdue:   { label: 'Gecikmiş',       color: 'bg-red-100 text-red-700' },
};

export default function Invoices() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');

  const load = () => {
    setLoading(true);
    const params = {};
    if (statusFilter) params.status = statusFilter;
    getInvoices(params)
      .then((r) => setInvoices(r.data))
      .catch(() => alert('Faturalar yüklenemedi'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [statusFilter]);

  const markSent = async (id) => {
    try { await updateInvoiceStatus(id, { status: 'sent' }); load(); } catch { alert('Güncelleme başarısız'); }
  };

  const markCollected = async (id) => {
    const d = prompt('Tahsilat tarihi (YYYY-MM-DD):') || new Date().toISOString().split('T')[0];
    try { await updateInvoiceStatus(id, { status: 'collected', collected_date: d }); load(); } catch { alert('Güncelleme başarısız'); }
  };

  const softDelete = async (id) => {
    if (!window.confirm('Silmek istediğinizden emin misiniz?')) return;
    try { await deleteInvoice(id); load(); } catch { alert('Silme başarısız'); }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Faturalar</h1>
        <Link to="/finance/invoices/new" className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 transition">+ Yeni Fatura</Link>
      </div>

      <div className="flex gap-2 flex-wrap">
        {[['', 'Tümü'], ['draft', 'Taslak'], ['sent', 'Gönderildi'], ['awaiting', 'Beklemede'], ['collected', 'Tahsil Edildi'], ['overdue', 'Gecikmiş']].map(([val, label]) => (
          <button key={val} onClick={() => setStatusFilter(val)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition ${statusFilter === val ? 'bg-blue-600 text-white' : 'bg-white border border-gray-200 text-gray-600 hover:border-blue-300'}`}>
            {label}
          </button>
        ))}
      </div>

      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden overflow-x-auto">
        {loading ? (
          <div className="flex justify-center items-center h-40"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" /></div>
        ) : invoices.length === 0 ? (
          <div className="text-center py-16 text-gray-400"><p className="text-4xl mb-2">🧾</p><p>Fatura bulunamadı</p></div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wide border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left">Fatura No</th>
                <th className="px-4 py-3 text-left">Tür</th>
                <th className="px-4 py-3 text-right">Hizmet</th>
                <th className="px-4 py-3 text-right">Toplam</th>
                <th className="px-4 py-3 text-center">Durum</th>
                <th className="px-4 py-3 text-right">Vade</th>
                <th className="px-4 py-3 text-center">İşlem</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {invoices.map((inv) => {
                const st = STATUS_LABELS[inv.status] || STATUS_LABELS.draft;
                return (
                  <tr key={inv.id} className="hover:bg-gray-50 transition">
                    <td className="px-4 py-3 font-mono text-blue-600 font-medium"><Link to={`/finance/invoices/${inv.id}`} className="hover:underline">{inv.invoice_number}</Link></td>
                    <td className="px-4 py-3 text-gray-500 capitalize">{inv.type}</td>
                    <td className="px-4 py-3 text-right text-gray-700">{fmt(inv.service_amount, inv.currency)}</td>
                    <td className="px-4 py-3 text-right font-semibold text-gray-900">{fmt(inv.total_amount, inv.currency)}</td>
                    <td className="px-4 py-3 text-center">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${st.color}`}>{st.label}</span>
                    </td>
                    <td className="px-4 py-3 text-right text-gray-500">{inv.due_date ? new Date(inv.due_date).toLocaleDateString('tr-TR') : '—'}</td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex items-center justify-center gap-1">
                        {inv.status === 'draft' && <button onClick={() => markSent(inv.id)} className="text-xs bg-blue-50 text-blue-600 px-2 py-1 rounded hover:bg-blue-100 transition">Gönder</button>}
                        {(inv.status === 'sent' || inv.status === 'awaiting') && <button onClick={() => markCollected(inv.id)} className="text-xs bg-green-50 text-green-600 px-2 py-1 rounded hover:bg-green-100 transition">Tahsil Et</button>}
                        <button onClick={() => softDelete(inv.id)} className="text-xs bg-red-50 text-red-500 px-2 py-1 rounded hover:bg-red-100 transition">Sil</button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
